import {
  ClipboardIcon,
  ExternalLinkIcon,
  EyeIcon,
  EyeOffIcon,
} from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';
import { getTeamsUserEmail } from '../../services/teams.js';

/**
 * Customertimes TestRail org. Pre-filled because the bridge is single-tenant
 * for one org — TestRail Cloud doesn't expose OAuth for the REST API (only
 * Basic Auth with email + API key), so the closest we can get to a
 * Jira-style "Connect" flow is: pre-fill the URL, surface a one-click jump
 * into TestRail's `My Settings → API Keys`, and let the user paste the
 * generated key back into this form. If a future tenant needs a different
 * instance, lift this to a server-provided config.
 */
const DEFAULT_TESTRAIL_URL = 'https://customertimesus.testrail.io/';

interface FormErrors {
  baseUrl?: string;
  email?: string;
  apiKey?: string;
  submit?: string;
}

/**
 * MEDA-692: this field accepted a browser PAGE url — the address bar of a
 * TestRail screen, e.g. `https://your-org.testrail.io/index.php?/suites/overview/1`
 * — and it was stored verbatim. Every consumer appends `/index.php?/api/v2/...`
 * to it, so the path doubled and TestRail answered `400 Invalid characters in
 * URI` on every call. In chat that surfaced as "the suite does not exist".
 *
 * What is rejected is narrow on purpose: a page url is recognised by the
 * `index.php` entry point, or by a query string or fragment. A PATH is NOT
 * rejected — self-hosted TestRail Server legitimately lives under one
 * (`https://tr.internal/testrail`), and refusing those would break the very
 * deployments this is meant to protect. Same rule as the normalizer in
 * cti-identity-service, so the form and the backend agree on what is valid.
 */
function validate(values: api.TestRailCredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  try {
    const url = new URL(values.baseUrl);
    if (!/^https?:$/.test(url.protocol)) {
      errors.baseUrl = 'Use http:// or https://';
    } else if (
      url.pathname.toLowerCase().includes('/index.php') ||
      url.search !== '' ||
      url.hash !== ''
    ) {
      errors.baseUrl =
        'Paste your TestRail address (https://your-org.testrail.io), not a link to a page inside it';
    }
  } catch {
    errors.baseUrl = 'Enter a valid URL (https://your-org.testrail.io)';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'Enter a valid email';
  }
  if (values.apiKey.length < 8) {
    errors.apiKey = 'API key looks too short';
  }
  return errors;
}

export function TestRailCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [values, setValues] = useState<api.TestRailCredentialsPayload>(() => ({
    baseUrl: DEFAULT_TESTRAIL_URL,
    // Pre-fill with the user's Teams email (Customertimes tenant — usually
    // identical to the TestRail login). Empty string when Teams hasn't
    // resolved yet; the user can edit freely either way.
    email: getTeamsUserEmail() ?? '',
    apiKey: '',
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const handleChange =
    (field: keyof api.TestRailCredentialsPayload) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => ({ ...v, [field]: e.target.value }));
      if (errors[field]) setErrors((es) => ({ ...es, [field]: undefined }));
    };

  // Read the clipboard and stuff the value into the API key field. Kept
  // as a button alongside the input because users have reported the
  // native Cmd/Ctrl+V occasionally not landing in this specific input
  // (suspected interaction with the surrounding Radix Dialog focus scope
  // / password-input quirks). The Clipboard API ask runs on a user
  // gesture, so the browser permission prompt is fine.
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((v) => ({ ...v, apiKey: text.trim() }));
      if (errors.apiKey) setErrors((es) => ({ ...es, apiKey: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  // Open the TestRail API-keys page in a new tab. Inside the Teams iframe
  // `window.open(url, '_blank')` is routed by Teams to the user's external
  // browser, so the TestRail email/password login (no SSO configured on
  // this instance) runs in the full browser context and the user can
  // generate a key in My Settings → API Keys, then paste it back here.
  const openApiKeysPage = (): void => {
    const target = (values.baseUrl || DEFAULT_TESTRAIL_URL).replace(/\/+$/, '');
    window.open(
      `${target}/index.php?/mysettings`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const v = validate(values);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      if (explainUnavailableActions)
        notify.error(
          Object.values(v)[0] ?? 'Complete the required connection fields.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveTestRailCredentials(values, workspaceId, details);
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="testrail-baseUrl" className="text-xs">
          Instance URL
        </Label>
        <Input
          id="testrail-baseUrl"
          type="url"
          inputMode="url"
          autoComplete="url"
          placeholder="https://your-org.testrail.io"
          value={values.baseUrl}
          onChange={handleChange('baseUrl')}
          aria-invalid={Boolean(errors.baseUrl)}
          aria-describedby={errors.baseUrl ? 'testrail-baseUrl-err' : undefined}
        />
        {errors.baseUrl && (
          <p id="testrail-baseUrl-err" className="text-xs text-destructive">
            {errors.baseUrl}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="testrail-email" className="text-xs">
          Email
        </Label>
        <Input
          id="testrail-email"
          type="email"
          autoComplete="email"
          placeholder="qa@your-org.com"
          value={values.email}
          onChange={handleChange('email')}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'testrail-email-err' : undefined}
        />
        {errors.email && (
          <p id="testrail-email-err" className="text-xs text-destructive">
            {errors.email}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="testrail-apiKey" className="text-xs">
            API key
          </Label>
          <button
            type="button"
            onClick={openApiKeysPage}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:underline"
          >
            <ExternalLinkIcon
              className="size-3"
              strokeWidth={2}
              aria-hidden="true"
            />
            Open TestRail API Keys
          </button>
        </div>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="testrail-apiKey"
              type={showKey ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="TestRail API key"
              value={values.apiKey}
              onChange={handleChange('apiKey')}
              aria-invalid={Boolean(errors.apiKey)}
              aria-describedby={
                errors.apiKey ? 'testrail-apiKey-err' : 'testrail-apiKey-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              aria-label={showKey ? 'Hide key' : 'Show key'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showKey ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.apiKey ? (
          <p id="testrail-apiKey-err" className="text-xs text-destructive">
            {errors.apiKey}
          </p>
        ) : (
          <p
            id="testrail-apiKey-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Sign in to TestRail, then{' '}
            <span className="font-medium">
              My Settings → API Keys → Add Key
            </span>
            . Paste the generated key here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
